import type { GetRiskAssessmentResultConfigByIdsResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetRiskAssessmentResultConfigByIdsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetRiskAssessmentResultConfigByIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { createQueryHook } from 'src/utils';

import {
  type RiskAssessmentResultConfig,
  riskAssessmentResultConfigSchema,
} from './useGetLatestRiskAssessmentResultConfig';

type UseGetByIdsArgs = {
  ids: string[];
};

const useGetByIdsBase = createQueryHook<
  UseGetByIdsArgs,
  GetRiskAssessmentResultConfigByIdsResponseRow[],
  GetRiskAssessmentResultConfigByIdsQuery
>({
  trpcQueryOptions: (trpc, { ids }) =>
    trpc.frontend.riskAssessmentResultConfig.getByIds.queryOptions({ ids }),
  mapTrpcDataToGraphQL: (data) => ({
    risk_assessment_result_config: data ?? [],
  }),
  graphqlDocument: GetRiskAssessmentResultConfigByIdsDocument,
  graphqlVariables: ({ ids }) => ({ ids }),
  graphqlFetchPolicy: 'no-cache',
});

export const useGetRiskAssessmentResultConfigByIds = (
  ids: string[],
  options?: { skip?: boolean }
) => {
  const { data, loading } = useGetByIdsBase({
    queryArgs: { ids },
    shouldSkip: options?.skip || ids.length === 0,
  });

  const configById = useMemo(() => {
    const map = new Map<string, RiskAssessmentResultConfig>();
    const records = data?.risk_assessment_result_config;
    if (!records) {
      return map;
    }

    for (const record of records) {
      if (record.Config) {
        map.set(
          record.Id,
          riskAssessmentResultConfigSchema.parse(record.Config)
        );
      }
    }

    return map;
  }, [data]);

  return { configById, loading };
};
